
// imports
import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { ReactiveFormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Globals } from './globals';

import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

// components
import { AppComponent } from './app.component';
import { HeaderComponent } from './header/header.component';
import { FooterComponent } from './footer/footer.component';
import { BreadcrumbsComponent } from './breadcrumbs/breadcrumbs.component';
import { LocationPageComponent } from './journey/location-page/location-page.component';
import { LeafletMapComponent } from './support-components/leaflet-map/leaflet-map.component';
import { BookingDetailsComponent } from './journey/booking-details/booking-details.component';

// Material
import { MatCheckboxModule } from '@angular/material';
import { MatButtonModule } from '@angular/material';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatCardModule } from '@angular/material/card';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTabsModule } from '@angular/material/tabs';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatNativeDateModule } from '@angular/material/core';
import { FormsModule } from '@angular/forms';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MarkdownModule } from 'ngx-markdown';
import { MAT_DATE_LOCALE } from '@angular/material'

import { ApplicationDetailsComponent } from './journey/application-details/application-details.component';
import { ChecklistComponent } from './journey/checklist/checklist.component';
import { TncsComponent } from './journey/tncs/tncs.component';
import { ConfirmationComponent } from './journey/confirmation/confirmation.component';
import { TranslateSplitPipe } from './pipes/translate-split.pipe';
import { CalendarComponent } from './support-components/calendar/calendar.component';
import { SanitiseHtmlPipe } from './pipes/sanitise-html.pipe';
import { BookingSummaryCardComponent } from './support-components/booking-summary-card/booking-summary-card.component';

import { InternationalPhoneNumberModule } from 'ngx-international-phone-number';

export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient, './assets/i18n/', '.json');
}

import { Ng2TelInputModule } from 'ng2-tel-input';
// import bn-ng-idle service
import { BnNgIdleService } from 'bn-ng-idle';
import { SessionComponent } from './session/session.component';
import { CustomDobComponent } from './support-components/custom-dob/custom-dob.component';
import { SkipToContentComponent } from './support-components/skip-to-content/skip-to-content.component';
import { PaymentComponent } from './journey/payment/payment/payment.component';

import { CookieService } from "ngx-cookie-service";

import { HttpInterceptorInterceptor } from './services/http-interceptor.interceptor';
import { ElavonHPPComponent } from './elavon-hpp/elavon-hpp.component';

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    BreadcrumbsComponent,
    LocationPageComponent,
    LeafletMapComponent,
    BookingDetailsComponent,
    ApplicationDetailsComponent,
    ChecklistComponent,
    TncsComponent,
    ConfirmationComponent,
    TranslateSplitPipe,
    CalendarComponent,
    SanitiseHtmlPipe,
    BookingSummaryCardComponent,
    SessionComponent,
    CustomDobComponent,
    SkipToContentComponent,
    ElavonHPPComponent,
    PaymentComponent
  ],
  imports: [
    NgbModule,
    BrowserModule,
    AppRoutingModule,
    MatNativeDateModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatButtonModule,
    MatInputModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatRadioModule,
    MatSelectModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatMenuModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatGridListModule,
    MatCardModule,
    MatStepperModule,
    MatTabsModule,
    MatExpansionModule,
    MatButtonToggleModule,
    MatChipsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatDialogModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    BrowserAnimationsModule,
    FormsModule,
    FlexLayoutModule,
    MarkdownModule.forRoot(),
    NgxSpinnerModule,
    BrowserModule,
    HttpClientModule,
    TranslateModule.forRoot({
      'loader': {
        'provide': TranslateLoader,
        'useFactory': HttpLoaderFactory,
        'deps': [HttpClient]
      }
    }),
    InternationalPhoneNumberModule,
    Ng2TelInputModule
  ],
  providers: [
    CookieService,
    Globals,
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    BnNgIdleService,
    {
      provide: HTTP_INTERCEPTORS, useClass:
        HttpInterceptorInterceptor, multi: true
    }
  ],
  bootstrap: [
    AppComponent
  ]
})
export class AppModule { }
